import { useMemo, useState } from 'react';
import { Plus, Tag } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/Button';
import { ArchivedCollapsibleSection } from '../../components/common/ArchivedCollapsibleSection';
import { EmptyState } from '../../components/common/EmptyState';
import { CategoryFormDialog } from '../../components/categories/CategoryFormDialog';
import { CategoryBucketsBoard } from '../../components/categories/CategoryBucketsBoard';
import { ClassifyOrphanDialog } from '../../components/categories/ClassifyOrphanDialog';
import { CategoryRow } from '../../components/categories/SortableCategoryList';
import {
  useArchiveCategoryMutation,
  useCategoriesQuery,
  useCreateCategoryMutation,
  useOrphanCheckQuery,
  useReorderCategoriesMutation,
  useUpdateCategoryMutation,
} from '../../features/expenses/queries';
import { PageContentShell } from '../../components/common/PageContentShell';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { payloadFromCategoryAllocation } from '../../utils/categoryAllocation';
import { extractMutationError } from '../../utils/apiError';
import pt from '../../locales/pt';

function ArchivedCategoryActions({ category, onEdit, onUnarchive }) {
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2 text-xs"
        onClick={() => onEdit(category)}
      >
        {pt.categories.actions.edit}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2 text-xs"
        onClick={() => onUnarchive(category)}
      >
        {pt.categories.actions.unarchive}
      </Button>
    </>
  );
}

export function Categories() {
  const { data: categories, isPending, isFetching } = useCategoriesQuery();
  const categoriesShell = useQueryShellState({ data: categories, isPending, isFetching });
  const categoriesList = useMemo(() => categories ?? [], [categories]);
  const { data: orphans = [] } = useOrphanCheckQuery();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultAllocation, setDefaultAllocation] = useState('essential');
  const [classifyCategory, setClassifyCategory] = useState(null);

  const createCategory = useCreateCategoryMutation();
  const updateCategory = useUpdateCategoryMutation();
  const archiveCategory = useArchiveCategoryMutation();
  const reorderCategories = useReorderCategoriesMutation();

  const activeCategories = useMemo(
    () =>
      categoriesList.filter((c) => !c.is_archived && !c.is_system_pot),
    [categoriesList],
  );
  const archivedCategories = useMemo(
    () => categoriesList.filter((c) => c.is_archived && !c.is_system_pot),
    [categoriesList],
  );

  const onSubmit = async (values) => {
    try {
      if (editing) {
        await updateCategory.mutateAsync({ id: editing.id, payload: values });
        toast.success('Categoria actualizada.');
      } else {
        await createCategory.mutateAsync(values);
        toast.success('Categoria criada.');
      }
      setFormOpen(false);
      setEditing(null);
      setDefaultAllocation('essential');
    } catch (err) {
      toast.error(extractMutationError(err, 'Erro ao guardar.'));
    }
  };

  const onArchive = async (category) => {
    try {
      await archiveCategory.mutateAsync(category.id);
      toast.success(pt.categories.archivedToast.replace('{name}', category.name));
    } catch (err) {
      toast.error(extractMutationError(err, 'Não foi possível arquivar.'));
    }
  };

  const onUnarchive = async (category) => {
    try {
      await updateCategory.mutateAsync({
        id: category.id,
        payload: { is_archived: false },
      });
      toast.success(pt.categories.restoredToast.replace('{name}', category.name));
    } catch (err) {
      toast.error(extractMutationError(err, 'Não foi possível reactivar.'));
    }
  };

  const openCreate = (allocation = 'essential') => {
    setEditing(null);
    setDefaultAllocation(allocation);
    setFormOpen(true);
  };

  const onReclassify = async (categoryId, allocation) => {
    await updateCategory.mutateAsync({
      id: categoryId,
      payload: payloadFromCategoryAllocation(allocation),
    });
  };

  const onClassifySubmit = async ({ id, payload }) => {
    try {
      await updateCategory.mutateAsync({ id, payload });
      toast.success(pt.categories.reclassifiedToast);
      setClassifyCategory(null);
    } catch (err) {
      toast.error(extractMutationError(err, pt.categories.reclassifyError));
    }
  };

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      <header className="hidden md:flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-semibold text-foreground md:text-2xl">
            {pt.categories.title}
          </h1>
          <p className="mt-0.5 text-xs text-muted-foreground md:text-sm">{pt.categories.subtitle}</p>
        </div>
        <Button size="sm" className="shrink-0" onClick={() => openCreate()}>
          <Plus className="h-4 w-4" aria-hidden /> {pt.categories.addNew}
        </Button>
      </header>

      <PageContentShell
        shells={[categoriesShell]}
        fallback={<SectionSkeleton variant="list" />}
      >
        {activeCategories.length === 0 && archivedCategories.length === 0 ? (
          <EmptyState
            icon={Tag}
            title={pt.categories.empty.title}
            description={pt.categories.empty.subtitle}
            action={
              <Button onClick={() => openCreate()}>{pt.categories.addFirst}</Button>
            }
          />
        ) : (
          <>
            {activeCategories.length === 0 ? (
              <p className="text-sm text-muted-foreground">{pt.categories.allArchivedHint}</p>
            ) : (
              <CategoryBucketsBoard
                categories={activeCategories}
                orphans={orphans}
                onReorder={(ids) => reorderCategories.mutateAsync(ids)}
              onReclassify={onReclassify}
              onEdit={(category) => {
                setEditing(category);
                setFormOpen(true);
              }}
              onArchive={onArchive}
              onAdd={(bucketId) => openCreate(bucketId)}
              onClassifyClick={setClassifyCategory}
            />
          )}

          <ArchivedCollapsibleSection
            title={pt.common.archivedSection.title}
            count={archivedCategories.length}
            className="overflow-hidden"
            hint={
              archivedCategories.length > 0
                ? pt.categories.archivedHint
                : undefined
            }
          >
            <ul className="flex flex-col gap-2">
              {archivedCategories.map((category) => (
                <li key={category.id} className="list-none">
                  <CategoryRow
                    category={category}
                    className="opacity-90"
                    actions={
                      <ArchivedCategoryActions
                        category={category}
                        onEdit={(c) => {
                          setEditing(c);
                          setFormOpen(true);
                        }}
                        onUnarchive={onUnarchive}
                      />
                    }
                  />
                </li>
              ))}
            </ul>
          </ArchivedCollapsibleSection>
        </>
        )}
      </PageContentShell>

      <CategoryFormDialog
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
          setDefaultAllocation('essential');
        }}
        onSubmit={onSubmit}
        initialValues={editing}
        defaultAllocation={defaultAllocation}
        isPending={createCategory.isPending || updateCategory.isPending}
      />

      <ClassifyOrphanDialog
        isOpen={Boolean(classifyCategory)}
        category={classifyCategory}
        onClose={() => setClassifyCategory(null)}
        onSubmit={onClassifySubmit}
        isPending={updateCategory.isPending}
      />

    </div>
  );
}
